import styled from "@emotion/styled";
import { Slide } from "@mui/material";
import { fontMerriweather, fontRoboto, transientOptions } from "@product/scmp-sdk";

import { MobileNewsletterPreview } from "~/components/newsletter/newsletter-list/mobile-newsletter-preview";
import { NewsletterPreview } from "~/components/newsletter/newsletter-list/newsletter-preview";
import type { WithStickyStackedHeightProps } from "~/lib/styles";
import { setFullViewportWidthStyles } from "~/lib/styles";

export const StyledMobileNewsletterPreview = styled(MobileNewsletterPreview)`
  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const StyledNewsletterPreview = styled(NewsletterPreview)`
  display: none;
  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;
  }
`;

export const Heading = styled.div`
  grid-area: Heading;

  margin-block-end: 40px;

  color: #001246;
  font-weight: 300;
  font-size: 36px;
  font-family: ${fontMerriweather};
  line-height: 45px;
  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    text-align: start;
  }
`;

export const PreviewContainer = styled.div<WithStickyStackedHeightProps>`
  grid-area: Preview;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 20}px;

  align-self: start;
`;

export const CategoryListContainer = styled.div<WithStickyStackedHeightProps>`
  grid-area: CategoryList;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 108}px;

  align-self: start;

  margin-block-start: 36px;
  margin-inline-end: 57px;
`;

export const CategoryItemListContainer = styled.div`
  grid-area: CategoryItemList;

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;

  margin-block-end: 72px;

  ${props => props.theme.breakpoints.only("tablet")} {
    margin-block-end: 0;
    margin-inline-end: 23px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    margin-block-end: 0;
    margin-inline-end: 16px;
  }
`;

export const CategoryItemListWrapper = styled.div`
  max-inline-size: 416px;

  ${props => props.theme.breakpoints.only("tablet")} {
    min-inline-size: 350px;
    max-inline-size: 400px;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    min-inline-size: 400px;
    max-inline-size: 389px;
  }
  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    inline-size: 416px;
    min-inline-size: 416px;
    max-inline-size: 424px;
  }
`;

type CategoryProps = {
  $isFocused?: boolean;
};
export const Category = styled("div", { ...transientOptions })<CategoryProps>`
  margin-block-end: 21px;

  color: ${props => (props.$isFocused ? "#001246" : "#999999")};
  font-weight: bold;
  font-size: 16px;
  font-family: ${fontRoboto};
  line-height: 19px;
  text-transform: uppercase;

  cursor: pointer;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 120px;
    margin-block-end: 24px;

    font-weight: inherit;
    font-family: inherit;
    text-transform: inherit;
  }
`;

export const Container = styled.div`
  display: grid;
  grid-template-areas:
    "Heading"
    "CategoryItemList";
  justify-content: center;

  margin-block: 40px 80px;

  ${CategoryListContainer}, ${PreviewContainer} {
    display: none;
  }

  ${props => props.theme.breakpoints.only("tablet")} {
    grid-template:
      "Heading Preview"
      "CategoryItemList Preview";
    grid-template-rows: repeat(2, min-content);
    /* Prevent layout shift by setting fix size */
    grid-template-columns: min-content calc(280px + 38px);

    ${PreviewContainer} {
      display: block;
    }
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid-template:
      "Heading Heading Preview"
      "CategoryList CategoryItemList Preview";
    grid-template-rows: repeat(2, min-content);
    /* Prevent layout shift by setting fix size */
    grid-template-columns: min-content min-content calc(363px + 27px);

    ${CategoryListContainer}, ${PreviewContainer} {
      display: block;
    }
  }
`;

export const NewsletterWidgetContainer = styled.div`
  ${setFullViewportWidthStyles()}

  position: fixed;
  inset-block-end: 0;

  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;

  inline-size: 100%;
  padding-block: 20px 40px;

  background: #ecf3ff;

  border-block-start: 2px solid #4585ff;
`;

export const StyledSlide = styled(Slide)`
  z-index: 1;
`;
